import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { UserContext } from './UserContext';

const ProfileContext = createContext();

export const ProfileProvider = ({ children }) => {
  const userCtx = useContext(UserContext);
  // Build a stable storage key per user (id/email/username fallback)
  const storageKey = useMemo(() => {
    const u = userCtx?.user ? { ...userCtx, ...userCtx.user } : userCtx;
    const uid = u?.id || u?.user_id || u?.email || u?.username || 'anon';
    return `owly:usageDateRange:${uid}`;
  }, [userCtx]);

  // Default to last day ("1d"), but hydrate from localStorage if present
  const [dateRange, setDateRange] = useState(() => {
    try {
      const v = typeof window !== 'undefined' ? localStorage.getItem('owly:usageDateRange:last') : null;
      const perUser = typeof window !== 'undefined' ? localStorage.getItem(storageKey) : null;
      return perUser || v || '1d';
    } catch (_) {
      return '1d';
    }
  });

  const updateDateRange = (newRange) => {
    setDateRange(newRange);
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem(storageKey, newRange);
        // also keep a last-used fallback (helps before user resolves)
        localStorage.setItem('owly:usageDateRange:last', newRange);
      }
    } catch (_) {}
  };

  // When storageKey changes (user switches), load persisted value
  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        const saved = localStorage.getItem(storageKey);
        if (saved && saved !== dateRange) {
          setDateRange(saved);
        } else if (!saved && dateRange !== '1d') {
          // Ensure default for new user
          setDateRange('1d');
        }
      }
    } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  return (
    <ProfileContext.Provider value={{ dateRange, updateDateRange }}>
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};
